import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { LogIn, Loader2, AlertTriangle } from 'lucide-react';

const Login = ({ setUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [sessionConflict, setSessionConflict] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [businessConfig, setBusinessConfig] = useState({
    mensaje_bienvenida: 'LICORSTORE RD',
    slogan: 'Sistema de Gestión de Licorería'
  });

  useEffect(() => {
    fetchBusinessConfig();
    // Mostrar mensaje si la sesion fue invalidada
    if (searchParams.get('session_expired') === '1') {
      toast.error('Su sesión fue cerrada porque se inició sesión en otro dispositivo.');
    }
  }, [searchParams]);

  const fetchBusinessConfig = async () => {
    try {
      const response = await axios.get(`${API}/public/system-info`);
      setBusinessConfig({
        mensaje_bienvenida: response.data.mensaje_bienvenida || 'LICORSTORE RD',
        slogan: response.data.slogan || 'Sistema de Gestión de Licorería'
      });
    } catch (error) {
      // Silenciosamente usar valores por defecto si falla
      console.error('Error al cargar configuración:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSessionConflict(false);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      const { access_token, user } = response.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);

      toast.success('¡Bienvenido!');
      navigate('/');
    } catch (error) {
      if (error.response?.status === 409) {
        setSessionConflict(true);
      } else {
        toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForceLogin = async () => {
    setLoading(true);
    setSessionConflict(false);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        ...formData,
        force_login: true,
      });
      const { access_token, user } = response.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);

      toast.success('Sesión anterior cerrada. ¡Bienvenido!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al forzar inicio de sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 p-4">
      <div className="w-full max-w-md">
        {/* Logo Section */}
        <div className="text-center mb-6 md:mb-8">
          <div className="inline-block p-3 md:p-4 bg-emerald-500 rounded-2xl shadow-lg mb-3 md:mb-4">
            <LogIn size={32} className="md:w-10 md:h-10 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" data-testid="login-title">
            {businessConfig.mensaje_bienvenida}
          </h1>
          <p className="text-sm md:text-base text-slate-300">{businessConfig.slogan}</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8" data-testid="login-form-card">
          <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-4 md:mb-6">Iniciar Sesión</h2>

          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
            <div>
              <Label htmlFor="username" className="text-slate-700 font-medium text-sm md:text-base">
                Usuario
              </Label>
              <Input
                id="username"
                type="text"
                placeholder="Ingrese su usuario"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                className="mt-1.5 h-10 md:h-11 text-sm md:text-base"
                data-testid="login-username-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium text-sm md:text-base">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Ingrese su contraseña"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="mt-1.5 h-10 md:h-11 text-sm md:text-base"
                data-testid="login-password-input"
              />
            </div>

            {sessionConflict && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4" data-testid="session-conflict-alert">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="text-amber-600 mt-0.5 flex-shrink-0" size={20} />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-800 mb-1">Sesión activa detectada</p>
                    <p className="text-sm text-amber-700 mb-3">
                      Este usuario ya tiene una sesión abierta en otro dispositivo. Debe cerrar esa sesión primero o puede forzar el cierre para iniciar aquí.
                    </p>
                    <Button
                      type="button"
                      onClick={handleForceLogin}
                      disabled={loading}
                      className="w-full h-10 bg-amber-600 hover:bg-amber-700 text-white font-semibold text-sm shadow"
                      data-testid="force-login-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="animate-spin mr-2" size={16} />
                          Cerrando sesión anterior...
                        </>
                      ) : (
                        'Cerrar sesión anterior e iniciar aquí'
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 md:h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm md:text-base shadow-lg"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={18} />
                  Iniciando sesión...
                </>
              ) : (
                'Ingresar'
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-200">
            <p className="text-sm text-slate-600 text-center italic">
              {businessConfig.slogan}
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-400 text-sm mt-8">
          © 2025 LICORSTORE RD - República Dominicana
        </p>
      </div>
    </div>
  );
};

export default Login;
